import React, { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Autocomplete,
  Chip,
  CircularProgress,
  LinearProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PhotoLibrary,
  Send,
  ArrowBack,
  ArrowForward,
} from '@mui/icons-material';
import { usePosts } from '../../context/posts.context';
import { useSnackbar } from '../../context/snackbar.context';
import { EXERCISE_NAMES, CALORIES_PER_MINUTE, WORKOUT_TYPE_LABELS } from '../../data/exercises.data';

const STEPS = ['Details', 'Exercises', 'Summary', 'Post'];

const EMPTY_SET = { reps: '', weight: '', unit: 'lbs' };
const EMPTY_EXERCISE = { name: '', sets: [{ ...EMPTY_SET }], notes: '' };

/**
 * Multi-step dialog for creating a workout post.
 * Step 1: Workout title, type, optional notes
 * Step 2: Add exercises with sets/reps/weight
 * Step 3: Summary with calculated stats
 * Step 4: Optional photo + caption, then post
 */
const CreateWorkoutPost = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { createPost, submitting, workoutPostOpen, closeWorkoutPost } = usePosts();
  const { showSnackbar } = useSnackbar();

  const [step, setStep] = useState(0);
  const [error, setError] = useState(null);

  // Step 1: Details
  const [workoutTitle, setWorkoutTitle] = useState('');
  const [workoutType, setWorkoutType] = useState('strength');
  const [duration, setDuration] = useState('');
  const [notes, setNotes] = useState('');

  // Step 2: Exercises
  const [exercises, setExercises] = useState([{ ...EMPTY_EXERCISE, sets: [{ ...EMPTY_SET }] }]);

  // Step 4: Photo + Caption
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [caption, setCaption] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const durationNum = parseInt(duration, 10) || 0;
  const caloriesBurned = Math.round(durationNum * (CALORIES_PER_MINUTE[workoutType] || 5));
  const validExercises = exercises.filter((ex) => ex.name.trim() !== '');

  // --- Step navigation ---
  const canGoNext = () => {
    if (step === 0) return workoutTitle.trim().length > 0;
    if (step === 1) return validExercises.length > 0;
    if (step === 2) return true;
    if (step === 3) return true;
    return true;
  };

  const handleNext = () => {
    if (step < STEPS.length - 1) setStep((s) => s + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  // --- Exercise management ---
  const handleExerciseNameChange = (index, value) => {
    setExercises((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], name: value || '' };
      return next;
    });
  };

  const handleAddExercise = () => {
    setExercises((prev) => [...prev, { ...EMPTY_EXERCISE, sets: [{ ...EMPTY_SET }] }]);
  };

  const handleRemoveExercise = (index) => {
    setExercises((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddSet = (exerciseIndex) => {
    setExercises((prev) => {
      const next = [...prev];
      next[exerciseIndex] = {
        ...next[exerciseIndex],
        sets: [...next[exerciseIndex].sets, { ...EMPTY_SET }],
      };
      return next;
    });
  };

  const handleRemoveSet = (exerciseIndex, setIndex) => {
    setExercises((prev) => {
      const next = [...prev];
      next[exerciseIndex] = {
        ...next[exerciseIndex],
        sets: next[exerciseIndex].sets.filter((_, i) => i !== setIndex),
      };
      return next;
    });
  };

  const handleSetChange = (exerciseIndex, setIndex, field, value) => {
    setExercises((prev) => {
      const next = [...prev];
      const sets = [...next[exerciseIndex].sets];
      sets[setIndex] = { ...sets[setIndex], [field]: value };
      next[exerciseIndex] = { ...next[exerciseIndex], sets };
      return next;
    });
  };

  const handleExerciseNotesChange = (index, value) => {
    setExercises((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], notes: value };
      return next;
    });
  };

  // --- Image handling ---
  const handleImageSelect = useCallback((event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please select a JPEG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Image is too large. Maximum size is 5MB.');
      return;
    }
    setError(null);
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);
    event.target.value = '';
  }, []);

  const handleRemoveImage = useCallback(() => {
    setImageFile(null);
    setImagePreview(null);
    setUploadProgress(0);
  }, []);

  // --- Submit ---
  const handleSubmit = useCallback(async () => {
    setError(null);
    setUploadProgress(0);
    try {
      const workoutData = {
        title: workoutTitle.trim(),
        duration: durationNum,
        caloriesBurned,
        exercises: validExercises.map((ex) => ({
          name: ex.name.trim(),
          sets: ex.sets
            .filter((s) => s.reps !== '' || s.weight !== '')
            .map((s) => ({
              reps: parseInt(s.reps, 10) || 0,
              weight: parseFloat(s.weight) || 0,
              unit: s.unit || 'lbs',
            })),
          notes: ex.notes || '',
        })),
        workoutType,
      };

      await createPost({
        type: 'workout',
        text: caption.trim(),
        imageFile: imageFile || undefined,
        onImageProgress: (progress) => setUploadProgress(progress),
        workout: workoutData,
      });

      // Reset and close
      resetForm();
      closeWorkoutPost();
      showSnackbar('Workout logged!', 'success');
    } catch (err) {
      setError(err.message || 'Failed to create workout post.');
    }
  }, [workoutTitle, durationNum, caloriesBurned, validExercises, workoutType, caption, imageFile, createPost, closeWorkoutPost, showSnackbar]);

  const resetForm = () => {
    setStep(0);
    setWorkoutTitle('');
    setWorkoutType('strength');
    setDuration('');
    setNotes('');
    setExercises([{ ...EMPTY_EXERCISE, sets: [{ ...EMPTY_SET }] }]);
    setImageFile(null);
    setImagePreview(null);
    setCaption('');
    setUploadProgress(0);
    setError(null);
  };

  const handleClose = useCallback(() => {
    if (submitting) return;
    resetForm();
    closeWorkoutPost();
  }, [submitting, closeWorkoutPost]);

  return (
    <Dialog
      open={workoutPostOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      fullScreen={isMobile}
      aria-labelledby="create-workout-dialog-title"
    >
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2} pb={1}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} id="create-workout-dialog-title">
          Log Workout
        </Typography>
        <IconButton edge="end" onClick={handleClose} disabled={submitting} aria-label="Close dialog">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Stepper */}
      <Box sx={{ px: 2, pb: 1 }}>
        <Stepper activeStep={step} alternativeLabel>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Details */}
        {step === 0 && (
          <Box>
            <TextField
              label="Workout Title"
              placeholder='e.g., "Push Day", "Morning Run"'
              value={workoutTitle}
              onChange={(e) => setWorkoutTitle(e.target.value)}
              fullWidth
              autoFocus
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Workout Type</InputLabel>
              <Select
                value={workoutType}
                onChange={(e) => setWorkoutType(e.target.value)}
                label="Workout Type"
              >
                {Object.entries(WORKOUT_TYPE_LABELS).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Duration (minutes)"
              value={duration}
              onChange={(e) => setDuration(e.target.value.replace(/\D/g, ''))}
              fullWidth
              type="text"
              inputMode="numeric"
              sx={{ mb: 2 }}
            />
            <TextField
              label="Notes (optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        )}

        {/* Step 2: Exercises */}
        {step === 1 && (
          <Box>
            {exercises.map((exercise, exIndex) => (
              <Box
                key={exIndex}
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Autocomplete
                    freeSolo
                    options={EXERCISE_NAMES}
                    value={exercise.name}
                    onInputChange={(_, val) => handleExerciseNameChange(exIndex, val)}
                    renderInput={(params) => (
                      <TextField {...params} label={`Exercise ${exIndex + 1}`} size="small" />
                    )}
                    sx={{ flex: 1 }}
                    size="small"
                  />
                  {exercises.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveExercise(exIndex)}
                      aria-label="Remove exercise"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>

                {/* Sets */}
                {exercise.sets.map((set, setIndex) => (
                  <Box key={setIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, pl: 1 }}>
                    <Typography variant="caption" sx={{ minWidth: 24 }}>
                      S{setIndex + 1}
                    </Typography>
                    <TextField
                      label="Reps"
                      value={set.reps}
                      onChange={(e) => handleSetChange(exIndex, setIndex, 'reps', e.target.value.replace(/\D/g, ''))}
                      size="small"
                      sx={{ width: 70 }}
                      inputMode="numeric"
                    />
                    <TextField
                      label="Weight"
                      value={set.weight}
                      onChange={(e) => handleSetChange(exIndex, setIndex, 'weight', e.target.value.replace(/[^0-9.]/g, ''))}
                      size="small"
                      sx={{ width: 80 }}
                      inputMode="decimal"
                    />
                    <Select
                      value={set.unit}
                      onChange={(e) => handleSetChange(exIndex, setIndex, 'unit', e.target.value)}
                      size="small"
                      sx={{ width: 70 }}
                    >
                      <MenuItem value="lbs">lbs</MenuItem>
                      <MenuItem value="kg">kg</MenuItem>
                    </Select>
                    {exercise.sets.length > 1 && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveSet(exIndex, setIndex)}
                        aria-label="Remove set"
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                ))}

                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => handleAddSet(exIndex)}
                  sx={{ textTransform: 'none', mt: 0.5, ml: 1 }}
                >
                  Add Set
                </Button>

                <TextField
                  label="Exercise notes (optional)"
                  value={exercise.notes}
                  onChange={(e) => handleExerciseNotesChange(exIndex, e.target.value)}
                  fullWidth
                  size="small"
                  sx={{ mt: 1 }}
                />
              </Box>
            ))}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddExercise}
              variant="outlined"
              fullWidth
              sx={{ textTransform: 'none' }}
            >
              Add Exercise
            </Button>
          </Box>
        )}

        {/* Step 3: Summary */}
        {step === 2 && (
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {workoutTitle}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Chip label={WORKOUT_TYPE_LABELS[workoutType]} color="primary" size="small" />
              {durationNum > 0 && <Chip label={`${durationNum} min`} variant="outlined" size="small" />}
              <Chip label={`~${caloriesBurned} cal`} variant="outlined" size="small" />
              <Chip label={`${validExercises.length} exercise${validExercises.length !== 1 ? 's' : ''}`} variant="outlined" size="small" />
            </Box>

            {validExercises.map((ex, i) => (
              <Box key={i} sx={{ py: 0.5, px: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {ex.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {ex.sets.filter((s) => s.reps || s.weight).length} set{ex.sets.filter((s) => s.reps || s.weight).length !== 1 ? 's' : ''}
                  {ex.notes && ` - ${ex.notes}`}
                </Typography>
              </Box>
            ))}

            {notes && (
              <Box sx={{ mt: 2, p: 1, borderRadius: 1, backgroundColor: 'action.hover' }}>
                <Typography variant="caption" color="text.secondary">
                  Notes: {notes}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Step 4: Photo + Caption */}
        {step === 3 && (
          <Box>
            <TextField
              label="Caption (optional)"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              fullWidth
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />

            <input
              ref={fileInputRef}
              hidden
              accept="image/jpeg,image/png,image/gif,image/webp"
              type="file"
              onChange={handleImageSelect}
            />

            {imagePreview ? (
              <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 2 }}>
                <Box
                  component="img"
                  src={imagePreview}
                  alt="Upload preview"
                  sx={{ width: '100%', maxHeight: 250, objectFit: 'cover', borderRadius: 2, display: 'block' }}
                />
                <IconButton
                  onClick={handleRemoveImage}
                  disabled={submitting}
                  sx={{
                    position: 'absolute', top: 8, right: 8,
                    backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                  size="small"
                  aria-label="Remove image"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Button
                startIcon={<PhotoLibrary />}
                onClick={() => fileInputRef.current?.click()}
                variant="outlined"
                sx={{ textTransform: 'none', mb: 2 }}
              >
                Add Photo
              </Button>
            )}

            {submitting && imageFile && uploadProgress > 0 && uploadProgress < 100 && (
              <Box sx={{ mb: 1 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
                <Typography variant="caption" color="text.secondary">
                  Uploading image... {uploadProgress}%
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer: navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <Button
          onClick={handleBack}
          disabled={step === 0 || submitting}
          startIcon={<ArrowBack />}
          sx={{ textTransform: 'none' }}
        >
          Back
        </Button>

        {step < STEPS.length - 1 ? (
          <Button
            onClick={handleNext}
            disabled={!canGoNext()}
            variant="contained"
            endIcon={<ArrowForward />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Next
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant="contained"
            endIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <Send />}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            {submitting ? 'Posting...' : 'Post Workout'}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default CreateWorkoutPost;
